'use client';

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { painelSwitchClass } from '@/components/painel/painel-styles';
import {
  SEO_DESC_GOOD_MAX,
  SEO_TITLE_GOOD_MAX,
  buildSeoHints,
  type SeoEntryFields,
  type SeoHintLevel,
} from '@/lib/seo';

type SeoFieldsEditorProps = {
  value: SeoEntryFields;
  onChange: (next: SeoEntryFields) => void;
  fallbackTitle?: string;
  fallbackDescription?: string;
  previewUrl?: string;
  siteName?: string;
  className?: string;
  disabled?: boolean;
};

function CharCount({ length, max, goodMax }: { length: number; max: number; goodMax: number }) {
  const tone =
    length === 0
      ? 'text-slate-500'
      : length > max
        ? 'text-rose-300'
        : length > goodMax
          ? 'text-amber-300'
          : 'text-emerald-300/80';
  return (
    <span className={cn('font-mono text-[11px] tabular-nums', tone)}>
      {length}/{max}
    </span>
  );
}

function HintBadge({ level, message }: { level: SeoHintLevel; message: string }) {
  const styles =
    level === 'good'
      ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200'
      : level === 'warn'
        ? 'border-amber-400/25 bg-amber-500/10 text-amber-100'
        : 'border-rose-400/25 bg-rose-500/10 text-rose-200';
  const label = level === 'good' ? 'Bom' : level === 'warn' ? 'Atenção' : 'Melhorar';
  return (
    <li className={cn('rounded-lg border px-3 py-2 font-inter text-xs leading-relaxed', styles)}>
      <span className="mr-2 font-poppins text-[10px] font-bold uppercase tracking-wide opacity-80">
        {label}
      </span>
      {message}
    </li>
  );
}

export function SeoFieldsEditor({
  value,
  onChange,
  fallbackTitle = '',
  fallbackDescription = '',
  previewUrl = 'https://ajuda.xat.com/exemplo',
  siteName = 'xat Ajuda',
  className,
  disabled,
}: SeoFieldsEditorProps) {
  const patch = (partial: Partial<SeoEntryFields>) => onChange({ ...value, ...partial });

  const displayTitle = (value.title || fallbackTitle || 'Título da página').trim();
  const displayDesc = (
    value.description ||
    fallbackDescription ||
    'Meta description aparecerá aqui…'
  ).trim();
  const ogTitle = (value.ogTitle || displayTitle).trim();
  const ogDesc = (value.ogDescription || displayDesc).trim();
  const ogImage = (value.ogImage || value.shareImage || '').trim();
  const twTitle = (value.twitterTitle || ogTitle).trim();
  const twDesc = (value.twitterDescription || ogDesc).trim();
  const twImage = (value.twitterImage || ogImage).trim();

  const hints = useMemo(
    () => buildSeoHints(value, fallbackTitle, fallbackDescription),
    [value, fallbackTitle, fallbackDescription]
  );

  const urlParts = previewUrl.replace(/^https?:\/\//, '').split('/');
  const host = urlParts[0] ?? 'ajuda.xat.com';
  const crumbs = urlParts.slice(1).filter(Boolean).join(' › ') || 'página';

  return (
    <div className={cn('space-y-6', className)}>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label className="font-poppins text-slate-400">SEO Title</Label>
              <CharCount length={value.title.length} max={70} goodMax={SEO_TITLE_GOOD_MAX} />
            </div>
            <Input
              disabled={disabled}
              value={value.title}
              onChange={(e) => patch({ title: e.target.value })}
              placeholder={fallbackTitle || 'Título para motores de busca'}
              className={painelInputClass}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label className="font-poppins text-slate-400">Meta Description</Label>
              <CharCount length={value.description.length} max={200} goodMax={SEO_DESC_GOOD_MAX} />
            </div>
            <Textarea
              disabled={disabled}
              value={value.description}
              onChange={(e) => patch({ description: e.target.value })}
              placeholder={fallbackDescription || 'Descrição curta e clara…'}
              className={cn(painelInputClass, 'min-h-[96px] resize-y')}
            />
          </div>

          <div className="space-y-2">
            <Label className="font-poppins text-slate-400">Canonical URL</Label>
            <Input
              disabled={disabled}
              value={value.canonicalUrl}
              onChange={(e) => patch({ canonicalUrl: e.target.value })}
              placeholder="https://…"
              className={painelInputClass}
            />
          </div>

          <div className="space-y-2">
            <Label className="font-poppins text-slate-400">Imagem de partilha</Label>
            <Input
              disabled={disabled}
              value={value.shareImage}
              onChange={(e) => patch({ shareImage: e.target.value })}
              placeholder="https://…/image.jpg"
              className={painelInputClass}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="font-poppins text-slate-400">Open Graph Title</Label>
              <Input
                disabled={disabled}
                value={value.ogTitle}
                onChange={(e) => patch({ ogTitle: e.target.value })}
                placeholder="Herda o SEO Title"
                className={painelInputClass}
              />
            </div>
            <div className="space-y-2">
              <Label className="font-poppins text-slate-400">OG Image</Label>
              <Input
                disabled={disabled}
                value={value.ogImage}
                onChange={(e) => patch({ ogImage: e.target.value })}
                placeholder="Herda imagem de partilha"
                className={painelInputClass}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="font-poppins text-slate-400">Open Graph Description</Label>
            <Textarea
              disabled={disabled}
              value={value.ogDescription}
              onChange={(e) => patch({ ogDescription: e.target.value })}
              placeholder="Herda a meta description"
              className={cn(painelInputClass, 'min-h-[72px] resize-y')}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="font-poppins text-slate-400">Twitter Title</Label>
              <Input
                disabled={disabled}
                value={value.twitterTitle}
                onChange={(e) => patch({ twitterTitle: e.target.value })}
                className={painelInputClass}
              />
            </div>
            <div className="space-y-2">
              <Label className="font-poppins text-slate-400">Twitter Image</Label>
              <Input
                disabled={disabled}
                value={value.twitterImage}
                onChange={(e) => patch({ twitterImage: e.target.value })}
                className={painelInputClass}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="font-poppins text-slate-400">Twitter Description</Label>
            <Textarea
              disabled={disabled}
              value={value.twitterDescription}
              onChange={(e) => patch({ twitterDescription: e.target.value })}
              className={cn(painelInputClass, 'min-h-[72px] resize-y')}
            />
          </div>

          <div className="flex flex-wrap gap-6 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
            <label className="flex items-center gap-2">
              <Switch
                disabled={disabled}
                checked={value.robotsIndex !== false}
                onCheckedChange={(v) => patch({ robotsIndex: v })}
                className={painelSwitchClass}
              />
              <span className="font-poppins text-sm text-slate-300">Indexar (index)</span>
            </label>
            <label className="flex items-center gap-2">
              <Switch
                disabled={disabled}
                checked={value.robotsFollow !== false}
                onCheckedChange={(v) => patch({ robotsFollow: v })}
                className={painelSwitchClass}
              />
              <span className="font-poppins text-sm text-slate-300">Seguir links (follow)</span>
            </label>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 bg-[#0b1020] p-4 shadow-inner">
            <p className="mb-3 font-poppins text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
              Preview Google
            </p>
            <p className="font-inter text-xl leading-snug text-[#8ab4f8]">{displayTitle}</p>
            <p className="mt-1 font-inter text-sm text-[#bdc1c6]">
              <span className="text-[#202124] dark:text-[#bdc1c6]">{host}</span>
              {crumbs ? <span className="text-slate-500"> › {crumbs}</span> : null}
            </p>
            <p className="mt-1 line-clamp-2 font-inter text-sm leading-relaxed text-[#bdc1c6]">
              {displayDesc}
            </p>
          </div>

          <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0b1020]">
            <p className="border-b border-white/10 px-4 py-2 font-poppins text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
              Open Graph
            </p>
            {ogImage ? (
              <div className="aspect-[1.91/1] bg-black/40">
                <img src={ogImage} alt="" className="h-full w-full object-cover" />
              </div>
            ) : (
              <div className="flex aspect-[1.91/1] items-center justify-center bg-white/[0.03] font-inter text-xs text-slate-500">
                Sem imagem
              </div>
            )}
            <div className="space-y-1 p-3">
              <p className="font-inter text-[10px] uppercase tracking-wide text-slate-500">{siteName}</p>
              <p className="font-poppins text-sm font-semibold text-white">{ogTitle}</p>
              <p className="line-clamp-2 font-inter text-xs text-slate-400">{ogDesc}</p>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0b1020]">
            <p className="border-b border-white/10 px-4 py-2 font-poppins text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
              Twitter / X
            </p>
            {twImage ? (
              <div className="aspect-[1.91/1] bg-black/40">
                <img src={twImage} alt="" className="h-full w-full object-cover" />
              </div>
            ) : (
              <div className="flex aspect-[1.91/1] items-center justify-center bg-white/[0.03] font-inter text-xs text-slate-500">
                Sem imagem
              </div>
            )}
            <div className="space-y-1 p-3">
              <p className="font-poppins text-sm font-semibold text-white">{twTitle}</p>
              <p className="line-clamp-2 font-inter text-xs text-slate-400">{twDesc}</p>
            </div>
          </div>

          <ul className="space-y-2">
            {hints.map((h) => (
              <HintBadge key={h.id} level={h.level} message={h.message} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
